import React, { useEffect, useState } from "react";
import appointmentsService from "../appointments/appointmentService";
import AuthorizationService from "../Authorization/authorizationService";
import DiagnosisCombinerService from "../Authorization/diagnosisCombinerService";
import PatientInfoService from "../patientInfo/patientInfoService";
import PointsBankAPI from "../points/PointsBankAPI";
import ReceiptAPI from "../points/ReceiptAPI";
import SearchPopup from "../popUps/searchPopup";
import "../appointments/apptReg.css";

const appointmentAPI = new appointmentsService();
const pointsBankAPI = new PointsBankAPI();
const receiptAPI = new ReceiptAPI();

const AppointmentScheduler = ({ preselectedOfficeId = null, patientId }) => {
  const [form, setForm] = useState({
    officeId: preselectedOfficeId || "",
    doctorId: "",
    doctorName: "",
    diagCombId: "",
    appointmentDate: "",
    appointmentTime: "",
    notes: "",
    typeVisit: "",
    conformation: "Scheduled",
  });

  const [availableTimes, setAvailableTimes] = useState([]);
  const [typeVisitOptions, setTypeVisitOptions] = useState([]);
  const [voucherOptions, setVoucherOptions] = useState([]);
  const [popup, setPopup] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (patientId) {
      loadDiagCombId(patientId);
      loadTypeVisit(patientId);
      loadVouchers(patientId);
    }
  }, [patientId]);

  useEffect(() => {
    const listener = (e) => {
      const { target, value, displayName } = e.data || {};
      if (target === "doctorId") {
        setForm((prev) => ({ ...prev, doctorId: value, doctorName: displayName }));
        setPopup(null);
      }
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  const loadDiagCombId = async (patientId) => {
    const authService = new AuthorizationService();
    const authId = await authService.getLatestAuthId(patientId);
    const diagService = new DiagnosisCombinerService();
    const diagId = await diagService.getDiagnosisIdByAuthId(authId);
    setForm((prev) => ({ ...prev, diagCombId: diagId }));
  };

  const loadTypeVisit = async (patientId) => {
    const authService = new AuthorizationService();
    const authId = await authService.getLatestAuthId(patientId);
    const insptId = await authService.getInsptIdByAuthId(authId);
    const patientService = new PatientInfoService();
    const options = await patientService.getActiveAsList(insptId);
    setTypeVisitOptions(options);
  };

  const loadVouchers = async (patientId) => {
    try {
      const pointsBankEntry = await pointsBankAPI.getTotalPoints(patientId);
      if (pointsBankEntry?.pointsBankId) {
        const receipts = await receiptAPI.getByPointsBankId(pointsBankEntry.pointsBankId);
        const validReceipts = receipts.filter(r => !r.vouched); // Assuming 'vouched' field
        const voucherList = validReceipts.map(receipt => ({
          id: receipt.id,
          name: receipt.name || `Voucher #${receipt.id}`
        }));

        setVoucherOptions(voucherList);
      }
    } catch (error) {
      console.error("Failed to load vouchers", error);
    }
  };

  const fetchAvailableTimes = async (officeId, date) => {
    try {
      const response = await appointmentAPI.getAvailableTimes(officeId, date);
      const slots = await response.json();
      setAvailableTimes(slots);
    } catch (err) {
      console.error("Error loading times", err);
    }
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateChange = async (date) => {
    handleChange("appointmentDate", date);
    if (form.officeId) {
      await fetchAvailableTimes(form.officeId, date);
    }
  };

  const handleTimeSelect = async (time) => {
    if (!availableTimes.includes(time)) {
      alert("Invalid time selected.");
      return;
    }

    const finalData = {
      ...form,
      appointmentTime: time,
    };

    try {
      const result = await appointmentAPI.addAppointment(finalData);
      if (result.success) {
        setConfirmation("Appointment successfully booked!");
      } else {
        alert("Failed to book: " + result.message);
      }
    } catch (error) {
      console.error("Booking error:", error);
      alert("Booking failed.");
    }
  };

  return (
    <div className="appointment-form">
      <h2>Schedule an Appointment</h2>

      {popup && (
        <SearchPopup
          target={popup.target}
          url={popup.url}
          onClose={() => setPopup(null)}
        />
      )}

      {confirmation && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={() => setConfirmation(null)}>&times;</span>
            <h3>{confirmation}</h3>
          </div>
        </div>
      )}

      <label>Office ID</label>
      <input
        type="text"
        value={form.officeId}
        onChange={(e) => handleChange("officeId", e.target.value)}
        disabled={!!preselectedOfficeId}
      />

      <label>Doctor</label>
      <div className="input-container">
        <button
          type="button"
          onClick={() =>
            setPopup({
              target: "doctorId",
              url: `/doctor/search?officeId=${form.officeId}`
            })
          }
          disabled={!form.officeId}
        >
          Search Doctor
        </button>
        {form.doctorName && <p>Selected: Dr. {form.doctorName}</p>}
      </div>

      <label>Date</label>
      <input
        type="date"
        value={form.appointmentDate}
        onChange={(e) => handleDateChange(e.target.value)}
      />

      {availableTimes.length > 0 && (
        <>
          <label>Available Time Slots</label>
          <div className="time-slot-list">
            {availableTimes.map((time, i) => (
              <button key={i} type="button" onClick={() => handleTimeSelect(time)}>
                {time}
              </button>
            ))}
          </div>
        </>
      )}

      <label>Notes</label>
      <textarea
        value={form.notes}
        onChange={(e) => handleChange("notes", e.target.value)}
        placeholder="Notes for appointment"
      />

      <label>Type of Visit</label>
      <select
        value={form.typeVisit}
        onChange={(e) => handleChange("typeVisit", e.target.value)}
      >
        <option value="">Select a type</option>

        {/* Regular Type Visits */}
        {typeVisitOptions.map((v, i) => (
          <option key={`type-${i}`} value={v}>
            {v}
          </option>
        ))}

        {/* Voucher Options */}
        {voucherOptions.length > 0 && (
          <>
            <option disabled>── Available Vouchers ──</option>
            {voucherOptions.map((v, i) => (
              <option key={`voucher-${i}`} value={`Voucher-${v.id}`}>
                🎟️ {v.name}
              </option>
            ))}
          </>
        )}
      </select>
    </div>
  );
};

export default AppointmentScheduler;
